import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import dynamic from 'next/dynamic';

import { Tabs } from 'antd';
import styles from '../MarketIndex/MarketDetail/index.module.scss';
import Tags from '../common/Tags';
import Cards from '../common/Cards';
import Vivawest from '../common/Vivawest';
import {
  getFilteredHousing,
  loadMoreFiltersOffers,
  loadMoreOffers,
  setCurrentPlace,
  setHousingOffers,
} from '../../store/market/actions';
import Paginator from '../common/Paginator';
import {
  housingOffersSelector,
  searchPropertyListSelector,
  selectedFiltersSelector,
  loadingSelector,
} from '../../store/market/selectors';
import {
  fetchWohnlageScoresForListings,
  fetchCultureScoresForListings,
  fetchLocalSupplyScoresForListings,
  fetchPublicTransportScoresForListings,
} from '../../utils/page';

import {
  DEFAULT_CENTER_MAP,
  DEFAULT_ZOOM_MAP,
  PAGE_SIZE,
  smallIndent,
} from '../../utils/constants';
import Filters from '../common/Filters';
import isValidKgsTags from '../../utils/helpers/validKgsTags';
import usePluralize from '../../utils/hooks/usePluralize';
import Loader from '../common/Loader';
import 'antd/dist/antd.css';

import LagescoresOptionsLayer from '../common/LagescoresOptionsLayer';
import useWindowDimensions from '../../utils/hooks/useWindowDimensions';
const Map = dynamic(() => import('../../components/common/Map/index'), {
  ssr: false,
});
const { TabPane } = Tabs;

const Properties = ({ result }) => {
  const { listings, parentData, tags } = result;

  const dispatch = useDispatch();
  const selectedFilters = useSelector(selectedFiltersSelector);
  const selectedSearchProperty = useSelector(searchPropertyListSelector);
  const storeLoadingState = useSelector(loadingSelector);

  const offers = useSelector(housingOffersSelector);
  const [coords, setCoords] = useState(DEFAULT_CENTER_MAP);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM_MAP);
  const [validTags, setValidTags] = useState([]);
  const [layout, setLayout] = useState(1);
  const [sortedOffers, setSortedOffers] = useState({
    items: [],
    loadingState: true,
  });
  const [sortOption, setSortOption] = useState(
    selectedFilters?.sortOption || '',
  );
  const { kgs } = parentData?.items?.[0] || '';

  const title = parentData?.items?.[0]?.fullTitle
    ? parentData.items?.[0]?.fullTitle
    : '';
  const coordsData = parentData?.items?.[0]?.center?.coordinates || '';

  const pluralize = usePluralize(
    offers?.numberOfItems,
    selectedSearchProperty.selectedHousing,
    selectedSearchProperty.selectedBuyOrRent,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOption, setSelectedOption] = useState('Wohnlage');
  const { height, width } = useWindowDimensions();
  // eslint-disable-next-line max-len
  const [mobileView, setMobileView] = useState(
    !!(height <= 1000 && width <= 500 && height != null && width != null),
  );
  const [timeCheck, setTimeCheck] = useState(false);
  const [goToLocation, setGotoLocation] = useState([]);

  useEffect(() => {
    setTimeout(() => {
      setTimeCheck(true);
    }, 0);
  }, []);

  useEffect(() => {
    setMobileView(
      !!(height <= 1000 && width <= 500 && height != null && width != null),
    );
  }, [height, width]);

  useEffect(() => {
    if (selectedFilters.selected || selectedSearchProperty.selected) {
      const fetchPage =
        offers?.numberOfItems !== listings?.numberOfItems &&
        offers?.numberOfItems !== listings?.numberOfItems &&
        offers?.page > 1 &&
        offers?.numberOfPages >= offers?.page
          ? offers.page
          : 1;
      setCurrentPage(fetchPage);
      dispatch(
        getFilteredHousing({
          kgs,
          rentOrBuy: selectedSearchProperty.buyOrRent,
          houseOrApartment: selectedSearchProperty.houseOrApartment,
          selectedFilters,
          currentPage: fetchPage,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  }, [
    selectedFilters.selected,
    selectedFilters?.rooms,
    selectedFilters?.priceFrom,
    selectedFilters?.priceTo,
    selectedFilters?.value,
    selectedFilters.operator,
    kgs,
    selectedSearchProperty.selected,
  ]);

  useEffect(() => {
    if (
      offers?.page > 1 &&
      offers?.numberOfPages < currentPage &&
      (selectedFilters.selected || selectedSearchProperty.selected)
    ) {
      const fetchPage =
        offers?.numberOfItems !== listings?.numberOfItems &&
        offers?.numberOfItems !== listings?.numberOfItems &&
        offers?.page > 1 &&
        offers?.numberOfPages >= offers?.page
          ? offers.page
          : 1;

      setCurrentPage(fetchPage);
      dispatch(
        getFilteredHousing({
          kgs,
          rentOrBuy: selectedSearchProperty.buyOrRent,
          houseOrApartment: selectedSearchProperty.houseOrApartment,
          selectedFilters,
          currentPage: 1,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers, listings]);

  useEffect(() => {
    setValidTags(isValidKgsTags(tags));
  }, [tags]);

  useEffect(() => {
    dispatch(setCurrentPlace(parentData?.items?.[0] || {}));
  }, [parentData, dispatch]);

  useEffect(() => {
    if (coordsData !== '') {
      setCoords({ lng: coordsData[0], lat: coordsData[1] });
      setZoom(12);
    }
  }, [coordsData]);

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      if (!selectedFilters.selected) {
        const payload = {
          kgs,
          rentOrBuy: selectedSearchProperty.buyOrRent,
          houseOrApartment: selectedSearchProperty.houseOrApartment,
          page,
        };
        dispatch(loadMoreOffers(payload));
      }
      if (selectedFilters.selected) {
        const payload = {
          kgs,
          rentOrBuy: selectedSearchProperty.buyOrRent,
          houseOrApartment: selectedSearchProperty.houseOrApartment,
          selectedFilters,
          page,
        };
        dispatch(loadMoreFiltersOffers(payload));
      }
    },
    [selectedSearchProperty, selectedFilters, kgs, dispatch],
  );
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (
      offers &&
      listings &&
      offers.page > 1 &&
      offers.numberOfItems === listings.numberOfItems &&
      !selectedFilters.selected
    ) {
      return setCurrentPage(offers.page);
    }
    if (
      listings?.items &&
      offers?.numberOfItems !== listings?.numberOfItems &&
      !selectedFilters.selected
    ) {
      dispatch(setHousingOffers(listings));
      return setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordsData, dispatch, listings, kgs]);

  // This hook will fetch the Wohnlagescore for the listings. Once the async function is finished, setSortedOffers
  // is set in this way, to be passed down to cards.
  // Listings load on first load if you visit /immobilien/city/haus-kaufen or switch between buy/rent. It currently
  // happens, that store offers & listings are different and this check prevents showing wrong data.
  useEffect(() => {
    let isLoaded = true; // if page changes we set this to false to avoid any missed cleanup effects
    setSortedOffers({ items: sortedOffers, loadingState: true });
    if (
      listings?.items?.[0]?.city !== offers?.items?.[0]?.city &&
      offers?.items?.length > 0
    ) {
      fetchWohnlageScoresForListings(listings, sortOption)
        .then((res) => fetchCultureScoresForListings(res))
        .then((res) => fetchLocalSupplyScoresForListings(res))
        .then((res) => fetchPublicTransportScoresForListings(res))
        .then((res) => {
          if (res?.length !== 0 && isLoaded) {
            setSortedOffers({ items: res, loadingState: false });
          }
        });
    } else if (offers?.items?.length === 0) {
      setSortedOffers({ items: [], loadingState: false });
    } else {
      fetchWohnlageScoresForListings(offers, sortOption)
        .then((res) => fetchCultureScoresForListings(res))
        .then((res) => fetchLocalSupplyScoresForListings(res))
        .then((res) => fetchPublicTransportScoresForListings(res))
        .then((res) => {
          if (res?.length !== 0 && isLoaded) {
            setSortedOffers({ items: res, loadingState: false });
          }
        });
    }

    return () => (isLoaded = false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers]);

  const handleSortAscending = useCallback(
    (sortOptionProperty) => {
      const itemsSortedByOption = sortedOffers.items.sort(
        (a, b) => a[sortOptionProperty] - b[sortOptionProperty],
      );
      setSortedOffers({ loadingState: false, items: itemsSortedByOption });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [sortedOffers.items],
  );

  const handleSortDescending = useCallback(
    (sortOptionProperty) => {
      const itemsSortedByOption = sortedOffers.items.sort(
        (a, b) => b[sortOptionProperty] - a[sortOptionProperty],
      );
      setSortedOffers({ loadingState: false, items: itemsSortedByOption });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [sortedOffers.items],
  );

  useEffect(() => {
    if (sortOption?.length > 0 && sortedOffers.items.length > 0) {
      if (sortOption === 'Preis aufsteigend') {
        handleSortAscending('price');
      } else if (sortOption === 'Preis absteigend') {
        handleSortDescending('price');
      } else if (sortOption === 'Größe aufsteigend') {
        handleSortAscending('livingSpace');
      } else if (sortOption === 'Größe absteigend') {
        handleSortDescending('livingSpace');
      } else if (sortOption === 'Baujahr aufsteigend') {
        handleSortAscending('livingSpace');
      } else if (sortOption === 'Baujahr absteigend') {
        handleSortDescending('yearOfConstruction');
      } else if (sortOption === 'Wohnlage aufsteigend') {
        handleSortAscending('wohnlage');
      } else if (sortOption === 'Wohnlage absteigend') {
        handleSortDescending('wohnlage');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOption, selectedFilters?.sortOption]);

  const handleSwitchLayout = () => {
    if (layout === 2) {
      return setLayout(1);
    }
    return setLayout(2);
  };

  const handleOnClick = (data) => {
    setSelectedOption(data);
  };

  const handleGoToLocation = (coordinates) => {
    setGotoLocation(coordinates);
  };

  return (
    <div className="container">
      <Filters sortOption={sortOption} setSortOption={setSortOption} />
      <h1
        data-cy="listings-count"
        className="g-color-gray-dark-v3 g-font-weight-700 mb-0 text-left"
      >
        <span className={styles.color}>
          {`${offers?.numberOfItems || ''} ${pluralize?.housing || ''} `}
        </span>
        {`${pluralize?.buyOrRent || ''} in ${title || ''}`}
      </h1>
      <div
        style={{
          marginBottom: `${smallIndent + 0.375}rem`,
          marginTop: `${smallIndent - 1}rem`,
        }}
      >
        <h2 className="g-color-gray-dark-v3 h3 mb-4 text-left text-uppercase font-weight-bold">
          Ausgewählte Stadtteile
        </h2>
        <Tags data={validTags} setCurrentPage={setCurrentPage} />
        {process.env.NEXT_PUBLIC_VIVAWEST_SWITCH === 'ON' && (
          <button
            style={{
              padding: '5px',
              backgroundColor: 'gray',
              opacity: '0.6',
              color: 'white',
              marginLeft: '10%',
            }}
            id="someButton"
            onClick={() => handleSwitchLayout()}
            type="button"
          >
            {layout === 1 ? 'SWITCH TO VIVAWEST' : 'SWITCH TO WOHNPREIS'} <br />{' '}
            (You might have to reload the page)
          </button>
        )}
      </div>
      <div
        style={{
          overflow: 'auto',
        }}
      >
        <LagescoresOptionsLayer handleOnClick={handleOnClick} />
      </div>

      {mobileView && timeCheck ? (
        <>
          <div>
            <Tabs defaultActiveKey="1" size="large" type="line" centered>
              <TabPane tab="LISTENANSICHT" key="1" forceRender>
                <div className={`'row no-gutters' ${styles.marketBlock}`}>
                  <div
                    className={classNames(
                      'display-flex direction-row!',
                      styles[
                        `${layout === 2 ? 'Vivawest' : 'offersContainer'}`
                      ],
                    )}
                  >
                    <div
                      className={`g-mb-50 g-mb-0--lg ${styles.textPositionLeftBlock}`}
                    >
                      {storeLoadingState || sortedOffers.loadingState ? (
                        <div style={{ position: 'relative', height: '500px' }}>
                          <Loader />
                        </div>
                      ) : (
                        <>
                          {process.env.NEXT_PUBLIC_VIVAWEST_SWITCH === 'ON' &&
                          layout === 2 ? (
                            <Vivawest
                              isImmobilienPage
                              detailList={false}
                              viva={layout}
                              newScore={sortedOffers}
                            />
                          ) : (
                            <Cards
                              isImmobilienPage
                              detailList={false}
                              viva={layout}
                              offers={sortedOffers}
                              selectedLayer={selectedOption}
                              goToLocation={handleGoToLocation}
                            />
                          )}
                        </>
                      )}
                    </div>
                    <Paginator
                      initialPage={currentPage}
                      onChangePage={handlePageChange}
                      total={offers?.numberOfItems || 0}
                      pageSize={PAGE_SIZE}
                    />
                  </div>
                </div>
              </TabPane>
              <TabPane tab="KARTENANSICHT" key="2">
                <div className={`'row no-gutters' ${styles.marketBlock}`}>
                  {(process.env.NEXT_PUBLIC_VIVAWEST_SWITCH === 'ON' ||
                    layout === 1) && (
                    <div
                      style={{ width: '45%', height: '730px' }}
                      className={`g-px-10 g-mb-50 g-mb-0--lg pr-0 ${styles.iframeBlockProperty}`}
                    >
                      <Map
                        center={coords}
                        zoom={zoom}
                        height={
                          height < 1000
                            ? `${(0.8 * height).toString()}px`
                            : '730px'
                        }
                        isShowLegend
                        isShowPOILegend
                        selectedLayer={selectedOption}
                        goToLocation={goToLocation}
                      />
                    </div>
                  )}
                </div>
              </TabPane>
            </Tabs>
          </div>
        </>
      ) : (
        <>
          <div
            className={`'row no-gutters' ${styles.marketBlock}`}
            style={{
              display: 'flex !important',
              flexDirection: 'row !important',
            }}
          >
            <div
              className={classNames(
                'display-flex direction-row',
                styles[`${layout === 2 ? 'Vivawest' : 'offersContainer'}`],
              )}
            >
              <div
                className={`g-mb-50 g-mb-0--lg ${styles.textPositionLeftBlock}`}
              >
                {storeLoadingState || sortedOffers.loadingState ? (
                  <div style={{ position: 'relative', height: '500px' }}>
                    <Loader />
                  </div>
                ) : (
                  <>
                    {process.env.NEXT_PUBLIC_VIVAWEST_SWITCH === 'ON' &&
                    layout === 2 ? (
                      <Vivawest
                        isImmobilienPage
                        detailList={false}
                        viva={layout}
                        newScore={sortedOffers}
                      />
                    ) : (
                      <Cards
                        isImmobilienPage
                        detailList={false}
                        viva={layout}
                        offers={sortedOffers}
                        selectedLayer={selectedOption}
                        goToLocation={handleGoToLocation}
                      />
                    )}
                  </>
                )}
              </div>
              <Paginator
                initialPage={currentPage}
                onChangePage={handlePageChange}
                total={offers?.numberOfItems || 0}
                pageSize={PAGE_SIZE}
              />
            </div>
            {(process.env.NEXT_PUBLIC_VIVAWEST_SWITCH === 'ON' ||
              layout === 1) && (
              <div
                style={{ width: '45%', height: '730px' }}
                className={`g-px-10 g-mb-50 g-mb-0--lg pr-0 ${styles.iframeBlockProperty}`}
              >
                <Map
                  center={coords}
                  zoom={zoom}
                  selectedLayer={selectedOption}
                  goToLocation={goToLocation}
                  isShowLegend
                  isShowPOILegend
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

Properties.propTypes = {
  result: PropTypes.object.isRequired,
};

export default Properties;
